import type {
  Client,
  ClientCheckInHistory,
  DeleteMemoryResult,
  IdentityMode,
  InteractionType,
  NotificationPrefs,
  Schedule,
  ScheduleInput,
  SchedulesResponse,
  SetIdentityModeResult,
  SetInteractionTypeResult,
  SubscriberMe,
  TriggerCallResult,
} from './types';

const baseUrl = import.meta.env.VITE_API_BASE_URL;
if (!baseUrl) {
  throw new Error('VITE_API_BASE_URL is not set');
}

async function authedGet<T>(path: string, getIdToken: () => Promise<string | null>): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error('not authenticated');

  const res = await fetch(`${baseUrl}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${path} failed: ${res.status} ${body}`);
  }
  return (await res.json()) as T;
}

export function getMe(getIdToken: () => Promise<string | null>): Promise<SubscriberMe> {
  return authedGet<SubscriberMe>('/subscriber/me', getIdToken);
}

export function getClients(getIdToken: () => Promise<string | null>): Promise<{ clients: Client[] }> {
  return authedGet<{ clients: Client[] }>('/subscriber/clients', getIdToken);
}

export function getClientCheckIns(
  memberId: string,
  getIdToken: () => Promise<string | null>,
): Promise<ClientCheckInHistory> {
  return authedGet<ClientCheckInHistory>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/check-ins`,
    getIdToken,
  );
}

async function authedMutate<T>(
  path: string,
  method: 'POST' | 'DELETE',
  getIdToken: () => Promise<string | null>,
): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error('not authenticated');

  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${path} failed: ${res.status} ${body}`);
  }
  return (await res.json()) as T;
}

export function triggerCall(
  memberId: string,
  getIdToken: () => Promise<string | null>,
): Promise<TriggerCallResult> {
  return authedMutate<TriggerCallResult>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/trigger-call`,
    'POST',
    getIdToken,
  );
}

export function deleteClientMemory(
  memberId: string,
  getIdToken: () => Promise<string | null>,
): Promise<DeleteMemoryResult> {
  return authedMutate<DeleteMemoryResult>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/memory`,
    'DELETE',
    getIdToken,
  );
}

async function authedPostJson<T>(
  path: string,
  body: unknown,
  getIdToken: () => Promise<string | null>,
): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error('not authenticated');

  const res = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${path} failed: ${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

export function updateNotificationPrefs(
  prefs: Partial<NotificationPrefs>,
  getIdToken: () => Promise<string | null>,
): Promise<NotificationPrefs> {
  return authedPostJson<NotificationPrefs>('/subscriber/notifications', prefs, getIdToken);
}

export function setClientInteractionType(
  memberId: string,
  interactionType: InteractionType,
  getIdToken: () => Promise<string | null>,
): Promise<SetInteractionTypeResult> {
  return authedPostJson<SetInteractionTypeResult>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/interaction-type`,
    { interactionType },
    getIdToken,
  );
}

export function setClientIdentityMode(
  memberId: string,
  identityMode: IdentityMode,
  getIdToken: () => Promise<string | null>,
): Promise<SetIdentityModeResult> {
  return authedPostJson<SetIdentityModeResult>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/identity-mode`,
    { identityMode },
    getIdToken,
  );
}

async function authedJson<T>(
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
  getIdToken: () => Promise<string | null>,
): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error('not authenticated');

  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${path} failed: ${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

// ---- Call schedules -------------------------------------------------------

export function getClientSchedules(
  memberId: string,
  getIdToken: () => Promise<string | null>,
): Promise<SchedulesResponse> {
  return authedGet<SchedulesResponse>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/schedules`,
    getIdToken,
  );
}

export function createClientSchedule(
  memberId: string,
  input: ScheduleInput,
  getIdToken: () => Promise<string | null>,
): Promise<{ schedule: Schedule }> {
  return authedJson<{ schedule: Schedule }>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/schedules`,
    'POST',
    input,
    getIdToken,
  );
}

export function updateClientSchedule(
  memberId: string,
  scheduleId: string,
  input: ScheduleInput,
  getIdToken: () => Promise<string | null>,
): Promise<{ schedule: Schedule }> {
  return authedJson<{ schedule: Schedule }>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/schedules/${encodeURIComponent(scheduleId)}`,
    'PUT',
    input,
    getIdToken,
  );
}

export function deleteClientSchedule(
  memberId: string,
  scheduleId: string,
  getIdToken: () => Promise<string | null>,
): Promise<{ ok: true }> {
  return authedMutate<{ ok: true }>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/schedules/${encodeURIComponent(scheduleId)}`,
    'DELETE',
    getIdToken,
  );
}

export function setClientTimezone(
  memberId: string,
  timezone: string,
  getIdToken: () => Promise<string | null>,
): Promise<{ ok: true; memberId: string; timezone: string }> {
  return authedPostJson<{ ok: true; memberId: string; timezone: string }>(
    `/subscriber/clients/${encodeURIComponent(memberId)}/timezone`,
    { timezone },
    getIdToken,
  );
}
